import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, User } from "lucide-react";
import { APPT_KINDS } from "@/lib/apptKinds";

const dayKey = (d) => {
  const dt = typeof d === "string" ? new Date(d) : d;
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const EMPTY = { title: "", start: "", end: "", location: "", notes: "", kind: "call", recurrence: "none" };

// Shared appointment create/edit dialog. Reused by the Planner and by "Book appointment" on a lead.
export function AppointmentDialog({ open, onClose, onSaved, initial, defaultDate, leadId, leadName }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState("one");
  const linkedName = initial?.lead_name || leadName || null;

  useEffect(() => {
    if (!open) return;
    setScope("one");
    if (initial) {
      setForm({ title: initial.title || "", start: (initial.start || "").slice(0, 16), end: (initial.end || "").slice(0, 16), location: initial.location || "", notes: initial.notes || "", kind: initial.kind || "call" });
    } else {
      const base = defaultDate ? `${dayKey(defaultDate)}T09:00` : "";
      setForm({ ...EMPTY, start: base, title: leadName ? `Call ${leadName}` : "" });
    }
  }, [open, initial, defaultDate, leadName]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Give the appointment a title");
    if (!form.start) return toast.error("Choose a start date & time");
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        start: form.start,
        start_at: form.start ? new Date(form.start).toISOString() : null,
        end: form.end || null,
        location: form.location,
        notes: form.notes,
        kind: form.kind,
        recurrence: initial ? "none" : form.recurrence,
        scope: initial?.series_id ? scope : "one",
        lead_id: leadId || initial?.lead_id || null,
        lead_name: linkedName,
      };
      if (initial) await api.patch(`/appointments/${initial.appointment_id}`, payload);
      else await api.post("/appointments", payload);
      toast.success(initial ? "Appointment updated" : "Appointment added to your calendar");
      onSaved && onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="appointment-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial ? "Edit appointment" : "New appointment"}</DialogTitle></DialogHeader>
        {linkedName && (
          <div className="flex items-center gap-2 rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary" data-testid="appt-linked-lead">
            <User className="h-4 w-4" /> Linked to lead: <span className="font-medium">{linkedName}</span>
          </div>
        )}
        <div className="space-y-4">
          {initial?.series_id && (
            <div className="rounded-lg border border-border p-3" data-testid="appt-scope">
              <Label>This is a repeating appointment. Apply changes to:</Label>
              <div className="mt-2 flex gap-2">
                <button type="button" onClick={() => setScope("one")} data-testid="scope-one" className={`flex-1 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${scope === "one" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>This appointment</button>
                <button type="button" onClick={() => setScope("future")} data-testid="scope-future" className={`flex-1 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${scope === "future" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>This &amp; future</button>
              </div>
            </div>
          )}
          <div><Label>Title</Label><Input value={form.title} onChange={set("title")} className="mt-1.5" placeholder="Call with client" data-testid="appt-title-input" /></div>
          <div>
            <Label>Type</Label>
            <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="appt-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(APPT_KINDS).map(([k, v]) => (
                  <SelectItem key={k} value={k}><span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: v.color }} /> {v.label}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label>Starts</Label><Input type="datetime-local" value={form.start} onChange={set("start")} className="mt-1.5" data-testid="appt-start-input" /></div>
            <div><Label>Ends <span className="font-normal text-muted-foreground">(optional)</span></Label><Input type="datetime-local" value={form.end} onChange={set("end")} className="mt-1.5" data-testid="appt-end-input" /></div>
          </div>
          <div><Label>Location <span className="font-normal text-muted-foreground">(optional)</span></Label><Input value={form.location} onChange={set("location")} className="mt-1.5" placeholder="Office, Zoom, phone…" data-testid="appt-location-input" /></div>
          <div><Label>Notes <span className="font-normal text-muted-foreground">(optional)</span></Label><Textarea value={form.notes} onChange={set("notes")} className="mt-1.5" rows={3} data-testid="appt-notes-input" /></div>
          {!initial && (
            <div>
              <Label>Repeat</Label>
              <Select value={form.recurrence} onValueChange={(v) => setForm((f) => ({ ...f, recurrence: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="appt-recurrence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Does not repeat</SelectItem>
                  <SelectItem value="weekly">Weekly (next 12 weeks)</SelectItem>
                  <SelectItem value="monthly">Monthly (next 12 months)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="appt-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {initial ? "Save" : "Add appointment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
